import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    return <Navigate to={user.role === "client" && user.assigned_client_ids?.[0]
      ? `/clients/${user.assigned_client_ids[0]}`
      : "/dashboard"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Bienvenido, ${u.full_name.split(" ")[0]}`);
      navigate(u.role === "client" && u.assigned_client_ids?.[0]
        ? `/clients/${u.assigned_client_ids[0]}`
        : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar sesión");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0a0e14] text-white relative overflow-hidden" data-testid="login-page">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 h-[500px] w-[500px] rounded-full bg-[#5EEAD4]/10 blur-[120px]" />
        <div className="absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full bg-[#0EA5E9]/10 blur-[120px]" />
      </div>

      {/* LEFT — branding panel */}
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 xl:p-16 relative z-10">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-white/5 backdrop-blur border border-white/10 flex items-center justify-center overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_data-sync-49/artifacts/mjp9j70a_logo_narwhal_curvas_no_text.png"
              alt="Narwhal"
              className="h-9 w-9 object-contain"
            />
          </div>
          <div className="leading-tight">
            <div className="font-display text-xl font-semibold tracking-tight">Narwhal</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5EEAD4]/80 font-medium">Agency Analytics</div>
          </div>
        </div>

        <div className="max-w-xl">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#5EEAD4] font-semibold mb-5">// Tu dirección de negocios</p>
          <h1 className="font-display font-light leading-[1.05] tracking-tight text-[44px] xl:text-[58px] mb-6">
            B2B<br />
            <span className="text-[#5EEAD4] font-normal italic">as a Service.</span>
          </h1>
          <p className="text-slate-400 text-base leading-relaxed max-w-md">
            El panel central donde convergen tus métricas de comunicación, SEO,
            paid media y outreach — en vivo, accionable, y compartible con tu equipo.
          </p>
        </div>

        <div className="flex items-center gap-6 text-[11px] text-slate-500">
          <span>© {new Date().getFullYear()} Narwhal Hub</span>
          <span className="h-1 w-1 rounded-full bg-slate-700" />
          <a href="https://www.narwhalhub.com" target="_blank" rel="noreferrer" className="hover:text-[#5EEAD4] transition-colors">narwhalhub.com</a>
        </div>
      </div>

      {/* RIGHT — form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="h-11 w-11 rounded-md bg-white/5 backdrop-blur border border-white/10 flex items-center justify-center overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_data-sync-49/artifacts/mjp9j70a_logo_narwhal_curvas_no_text.png" alt="Narwhal" className="h-9 w-9 object-contain" />
            </div>
            <div>
              <div className="font-display text-xl font-semibold tracking-tight">Narwhal</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#5EEAD4]/80 font-medium">Agency Analytics</div>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-7 shadow-[0_24px_60px_rgba(0,0,0,0.4)]">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#5EEAD4] font-semibold mb-2">// Ingresá</p>
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-1">Iniciá sesión</h2>
            <p className="text-xs text-slate-400 mb-6">Accedé con tus credenciales corporativas.</p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  placeholder="vos@empresa.com"
                  className="mt-1.5 h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-slate-600 focus-visible:border-[#5EEAD4]/60 focus-visible:ring-[#5EEAD4]/20"
                  data-testid="login-email"
                />
              </div>
              <div>
                <Label htmlFor="pw" className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Contraseña</Label>
                <Input
                  id="pw"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="mt-1.5 h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-slate-600 focus-visible:border-[#5EEAD4]/60 focus-visible:ring-[#5EEAD4]/20"
                  data-testid="login-password"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="group w-full h-11 mt-2 rounded-md bg-[#5EEAD4] hover:bg-[#7df3e0] active:bg-[#4dd6c0] disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-semibold text-sm tracking-tight transition-all flex items-center justify-center gap-2"
                data-testid="login-submit"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Entrar
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between text-[10px] text-slate-500">
              <span>Acceso restringido al staff Narwhal y clientes asignados.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
